(() => {
  const initConfigurator = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const state = {
      design: 'simple',
      car: '#15b8d8',
      stickerPrimary: '#ffffff',
      stickerSecondary: '#23b0e7',
      touched: {
        car: false,
        stickerPrimary: false,
        stickerSecondary: false
      }
    };

    const layer = (name) => root.querySelector(`[data-layer="${name}"]`);
    const inputs = {
      car: root.querySelector('[data-color-input="car"]'),
      stickerPrimary: root.querySelector('[data-color-input="sticker-primary"]'),
      stickerSecondary: root.querySelector('[data-color-input="sticker-secondary"]')
    };
    const finishNote = root.querySelector('[data-finish-note]');
    const primaryColorLabel = root.querySelector('[data-primary-color-label]');
    const configStatus = root.querySelector('[data-config-status]');

    const fetchSvg = async (url, target) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`SVG introuvable: ${url}`);
      target.innerHTML = await response.text();
      const svg = target.querySelector('svg');
      if (svg) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      }
    };

    const setFillAndStroke = (element, color, includeStroke = false) => {
      if (!element) return;
      element.querySelectorAll('path, polygon, rect, circle, ellipse, g').forEach((node) => {
        node.style.fill = color;
        if (includeStroke) node.style.stroke = color;
      });
    };

    const forceGroupColor = (svgRoot, selector, color) => {
      const group = svgRoot.querySelector(selector);
      if (!group) return;
      group.querySelectorAll('*').forEach((node) => {
        node.style.setProperty('fill', color, 'important');
        node.style.setProperty('stroke', color, 'important');
      });
    };

    const removeIfFound = (rootElement, selector) => {
      rootElement.querySelectorAll(selector).forEach((node) => node.remove());
    };

    const prepareMockupLayers = () => {
      const mockup = layer('mockup');
      const overlay = layer('mockup-overlay');
      if (!mockup || !overlay || overlay.children.length) return;

      overlay.innerHTML = mockup.innerHTML;
      const overlaySvg = overlay.querySelector('svg');
      if (!overlaySvg) return;

      removeIfFound(overlaySvg, '#carrosserie, #carroserie, #mockup_arriere, #POT');

      const bottomSvg = mockup.querySelector('svg');
      if (bottomSvg) {
        removeIfFound(bottomSvg, '#POT');
      }
    };

    const updateSvgColors = () => {
      prepareMockupLayers();

      const carShape = layer('mockup').querySelector('#carroserie');
      if (carShape) {
        carShape.style.fill = state.car;
        carShape.style.fillRule = 'evenodd';
        carShape.style.clipRule = 'evenodd';
        carShape.setAttribute('fill-rule', 'evenodd');
        carShape.setAttribute('clip-rule', 'evenodd');
      }

      const simple = layer('design-simple');
      setFillAndStroke(simple.querySelector('#couleur_1'), state.stickerPrimary, true);
      simple.querySelectorAll('.cls-1').forEach((node) => {
        node.style.fill = state.stickerPrimary;
        node.style.stroke = state.stickerPrimary;
      });

      const relief = layer('design-relief');
      forceGroupColor(relief, '#couleur_1', state.stickerPrimary);
      forceGroupColor(relief, '#couleur_2', state.stickerSecondary);
      relief.querySelectorAll('.cls-1').forEach((node) => {
        node.style.setProperty('fill', state.stickerPrimary, 'important');
        node.style.setProperty('stroke', state.stickerPrimary, 'important');
      });
      relief.querySelectorAll('.cls-2, .cls-3').forEach((node) => {
        node.style.setProperty('fill', state.stickerSecondary, 'important');
        node.style.setProperty('stroke', state.stickerSecondary, 'important');
      });
    };

    const updateDesign = () => {
      const isRelief = state.design === 'relief';
      layer('design-simple').classList.toggle('is-hidden', isRelief);
      layer('design-relief').classList.toggle('is-hidden', !isRelief);
      root.querySelectorAll('[data-relief-only]').forEach((node) => {
        node.classList.toggle('is-hidden', !isRelief);
        node.hidden = !isRelief;
      });
      root.querySelectorAll('[data-design-choice]').forEach((button) => {
        const active = button.dataset.designChoice === state.design;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (finishNote) {
        finishNote.textContent = isRelief
          ? 'Double couleur : supplement de 20 EUR.'
          : 'Double couleur : supplement de 20 EUR.';
      }

      if (primaryColorLabel) {
        primaryColorLabel.textContent = isRelief
          ? 'Couleur principale'
          : 'Couleur du design';
      }

      setAddToCartState();
    };

    const getProductForms = () => {
      const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
      const closestForm = root.closest('form[action*="/cart/add"]');
      if (closestForm && !forms.includes(closestForm)) forms.unshift(closestForm);
      return forms;
    };

    const getCurrentVariantId = () => {
      if (state.design === 'relief') return root.dataset.reliefVariantId;
      return root.dataset.simpleVariantId;
    };

    const setProductVariant = () => {
      const variantId = getCurrentVariantId();
      if (!variantId) return;

      getProductForms().forEach((form) => {
        const variantField = form.querySelector('[name="id"]');
        if (!variantField) return;

        if (variantField.value !== variantId) {
          variantField.value = variantId;
          variantField.dispatchEvent(new Event('input', { bubbles: true }));
          variantField.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    };

    const isConfigured = () => {
      if (!state.touched.car || !state.touched.stickerPrimary) return false;
      if (state.design === 'relief' && !state.touched.stickerSecondary) return false;
      return true;
    };

    const setAddToCartState = () => {
      const ready = isConfigured();

      getProductForms().forEach((form) => {
        form.querySelectorAll('button[type="submit"], input[type="submit"], button[name="add"]').forEach((button) => {
          button.disabled = !ready;
          button.setAttribute('aria-disabled', ready ? 'false' : 'true');
        });
      });

      if (configStatus) {
        configStatus.classList.toggle('is-ready', ready);
        configStatus.textContent = ready
          ? 'Configuration prete. Tu peux ajouter au panier.'
          : 'Choisis les couleurs pour activer l’ajout au panier.';
      }
    };

    const setLineItemProperty = (form, name, value) => {
      const selector = `input[name="properties[${name}]"]`;
      let input = form.querySelector(selector);

      if (!value) {
        if (input) input.remove();
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        form.appendChild(input);
      }

      input.value = value;
    };

    const ensureLineItemProperties = () => {
      const properties = {
        'Finition': state.design === 'relief' ? 'Double couleur (+20 EUR)' : 'Simple',
        'Couleur carrosserie': state.car,
        'Couleur du design': state.stickerPrimary,
        'Couleur secondaire': state.design === 'relief' ? state.stickerSecondary : ''
      };

      getProductForms().forEach((form) => {
        Object.entries(properties).forEach(([name, value]) => {
          setLineItemProperty(form, name, value);
        });
      });

      setProductVariant();
    };

    const render = () => {
      updateSvgColors();
      updateDesign();
      ensureLineItemProperties();
    };

    root.querySelectorAll('[data-design-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        state.design = button.dataset.designChoice;
        if (state.design === 'relief') state.touched.stickerSecondary = false;
        render();
      });
    });

    Object.entries(inputs).forEach(([key, input]) => {
      input.addEventListener('input', () => {
        state[key] = input.value;
        state.touched[key] = true;
        render();
      });
    });

    Promise.all([
      fetchSvg(root.dataset.mockupUrl, layer('mockup')),
      fetchSvg(root.dataset.simpleUrl, layer('design-simple')),
      fetchSvg(root.dataset.reliefUrl, layer('design-relief'))
    ]).then(render).catch((error) => {
      console.error(error);
    });

    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        ensureLineItemProperties();
        if (!isConfigured()) {
          event.preventDefault();
          setAddToCartState();
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, true);
  };

  const start = () => {
    document.querySelectorAll('[data-configurator]').forEach(initConfigurator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
